import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Table from "components/Common/dataTable";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import CustomAlert from "components/Common/ui/CustomAlert";
import { all_routes } from "router/all_routes";
import { ClassData } from "types/classTypes";
import { getBranchLabelFromCode, getClassLevelLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";

interface TeacherClassesTableProps {
  classes: ClassData[];
  loading: boolean;
  pagination: {
    page?: number;
    limit?: number;
    total?: number;
  } | null;
  error?: string | null;
  onChange: (pg: any) => void;
}

const TeacherClassesTable: React.FC<TeacherClassesTableProps> = ({
  classes,
  loading,
  pagination,
  error,
  onChange,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const columns: Array<{
    title: string;
    dataIndex?: keyof ClassData;
    sorter?: (a: ClassData, b: ClassData) => number;
    render?: (text: any, record: ClassData) => JSX.Element | string;
  }> = [
    {
      title: t("class"),
      dataIndex: "name",
    },
    {
      title: t("class_code"),
      dataIndex: "code",
    },
    {
      title: t("section"),
      dataIndex: "section",
      render: (_, record) => t(record.classLevel?.schoolCycle || ""),
    },
    {
      title: t("level"),
      dataIndex: "level",
      render: (_, record) => getClassLevelLabelFromCode(record.classLevel?.labelKey ?? ""),
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (_, record) =>
        record.branch
          ? getBranchLabelFromCode(record.branch?.labelKey ?? "", t)
          : t("no_branch"),
    },
    {
      title: t("no_of_students"),
      dataIndex: "noOfStudents",
      render: (_, record) => (
        <Link to={all_routes.detailClass.replace(":classId", record.id) + "?tab=students"}>
          {record.noOfStudents || 0}
        </Link>
      ),
    },
    {
      title: t("action"),
      render: (_, record) => {
        const actions: Action[] = [
          {
            label: t("students_list"),
            icon: "ti ti-eye",
            actionType: "link",
            hidden: !ability.can("read", "Student"),
            to: `${all_routes.myStudent}?classId=${record.id}`,
          },
        ];

        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  return (
    <div className="table-responsive">
      {error ? (
        <CustomAlert
          message={error}
          type="error"
          showIcon
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={classes}
          loading={loading}
          pagination={true}
          dataPaginate={{
            current: pagination?.page || 1,
            pageSize: pagination?.limit || 10,
            total: pagination?.total || 0,
          }}
          onChange={onChange}
        />
      )}
    </div>
  );
};

export default TeacherClassesTable;
